import React, { ComponentType, useCallback, useMemo } from 'react';

import createSettingsHOC, {
  Props,
  SharedProps,
} from 'src/components/settings/create-settings-hoc';
import { useParams } from 'src/router/hooks';
import { useWorkspaceContainerNavigationState } from 'src/sections/workspace/components/navigation/use-container-navigation-props';
import urls from 'src/sections/workspace/urls';

import WorkspaceSettingsBreadcrumbs from '../components/breadcrumbs';

const Breadcrumbs = (): JSX.Element => {
  const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
  const settingsHref = urls.ui.settings(workspaceSlug || '');
  return <WorkspaceSettingsBreadcrumbs settingsHref={settingsHref} />;
};

/**
 * in order to use the hooks we need to be inside a function that returns FC
 * so wrapping this in a dummy function component
 */
const withWorkspaceSettings = <P,>(
  Component: ComponentType<React.PropsWithChildren<P & Props & SharedProps>>,
  componentProps?: Props
) => {
  return (props: P & Props) => {
    const { selectedMenuItem } = useWorkspaceContainerNavigationState();

    // passing in a fn that returns selectedMenuItem as first arg to createSettingsHOC
    // even though this hook doesn't actually select anything out of redux state,
    // this is to minimise changes in createSettingsHOC, and it works because
    // useSelector will just return whatever is returned from selector function.
    const getSelectedMenuItemFromNavResourceState = useCallback(
      () => selectedMenuItem,
      [selectedMenuItem]
    );

    // memoizing to avoid unnecessary remounting which causes bugs
    const FinalComponent = useMemo(
      () =>
        createSettingsHOC(getSelectedMenuItemFromNavResourceState, {
          breadcrumbs: Breadcrumbs,
        })(Component, componentProps),
      [getSelectedMenuItemFromNavResourceState]
    );

    return <FinalComponent {...props} />;
  };
};

export default withWorkspaceSettings;
